import { useAuth, useClerk } from "@clerk/tanstack-react-start";
import * as Sentry from "@sentry/tanstackstart-react";
import { createFileRoute, useSearch } from "@tanstack/react-router";
import { useCallback, useEffect, useState } from "react";
import z from "zod";

import { validateRedirectPath } from "~/lib/shared/post-auth-routing";

export const Route = createFileRoute("/_auth/accept-token/")({
  // The redirect is followed with `location.replace`, so only a same-site
  // path may pass; anything else falls back to the default destination.
  validateSearch: z.object({
    token: z.string(),
    redirect: z
      .string()
      .optional()
      .transform((path) =>
        path ? (validateRedirectPath(path) ?? undefined) : undefined,
      ),
  }),
  component: AcceptTokenPage,
});

type AcceptTokenStatus = "signing-in" | "redirecting-to-login";

function AcceptTokenPage() {
  const [status, setStatus] = useState<AcceptTokenStatus>("signing-in");
  const search = useSearch({ from: "/_auth/accept-token/" });
  const token = (search?.token as string | undefined) ?? undefined;
  const redirect =
    (search?.redirect as string | undefined) ?? "/settings/password";

  const { client, setActive } = useClerk();
  const { isSignedIn } = useAuth();

  const failureRedirectUrl = (() => {
    // Summit agenda failures go to registration instead of sign-in
    if (redirect?.startsWith("/summit/agenda")) {
      return "/summit/register";
    }
    if (typeof window === "undefined") {
      return "/signin?from=accept-token-failure";
    }
    const loginUrl = new URL("/login", window.location.origin);
    loginUrl.searchParams.set("from", "accept-token-failure");
    if (redirect) {
      loginUrl.searchParams.set("redirect", redirect);
    }
    return loginUrl.toString();
  })();

  const redirectOnFailure = useCallback(() => {
    if (typeof window === "undefined") return;
    console.log("[accept-token] Redirecting to sign-in page", {
      failureRedirectUrl,
    });
    setStatus("redirecting-to-login");
    window.location.href = failureRedirectUrl;
  }, [failureRedirectUrl]);

  useEffect(() => {
    let cancelled = false;
    const run = async () => {
      // If the user is already signed in, just go to the redirect target
      if (isSignedIn && redirect) {
        window.location.replace(redirect);
        return;
      }

      if (!token) {
        if (!cancelled) redirectOnFailure();
        return;
      }

      if (!client) return;
      try {
        const attempt = await client.signIn.create({
          strategy: "ticket",
          ticket: token,
        });

        if (attempt.status === "complete" && attempt.createdSessionId) {
          await setActive({ session: attempt.createdSessionId });
          if (!cancelled) {
            window.location.replace(redirect);
          }
          return;
        }
        console.warn("[accept-token] Sign-in incomplete", {
          status: attempt.status,
        });
        if (!cancelled) redirectOnFailure();
      } catch (error) {
        console.error("[accept-token] Sign-in failed", error);
        Sentry.captureException(error, {
          tags: { surface: "auth_accept_token_signin" },
        });
        if (!cancelled) redirectOnFailure();
      }
    };
    void run();
    return () => {
      cancelled = true;
    };
  }, [token, client, setActive, redirect, isSignedIn, redirectOnFailure]);

  return (
    <div className="mx-auto max-w-md p-8">
      {status === "signing-in"
        ? "Signing you in…"
        : "We couldn't automatically sign you in. Redirecting to the login page…"}
    </div>
  );
}
